import React, { useEffect, useRef, useState } from "react";
import {
  Badge,
  Button,
  Divider,
  Flex,
  Input,
  Skeleton,
  Tooltip,
  Typography,
  theme,
  message as antdMessage,
} from "antd";
import {
  FilterOutlined,
  PlusOutlined,
  ShopOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { SellerIcon, WhatsAppMetaIcon, WhatsAppQrIcon } from "@/components/icon";
import { isCloudApi } from "@/services/channel-types";
import dayjs from "dayjs";
import {
  Channel,
  ConversationListItem,
  QuickFilter,
  conversationsApi,
} from "@/services/conversations.api";
import { ContactParticipation, contactsApi, phoneFromThread } from "@/services/contacts.api";
import { ownerParticipations } from "./ownerParticipations";
import { ChannelPickerDropdown } from "@/components/channel-picker";

const { Text } = Typography;

const QUICK: { key: QuickFilter; label: string }[] = [
  { key: "all", label: "Todos" },
  { key: "mine", label: "Míos" },
  { key: "no_operators", label: "Sin operadores" },
];

/**
 * Colores del ítem de la bandeja, según los cinco estados del Figma.
 *
 * Una conversación **cerrada** se atenúa entera —nombre incluido— y pierde el énfasis de no leído:
 * ya se resolvió, no tiene que competir por la atención con las abiertas.
 */
const ITEM = {
  open: { name: "#262626", muted: "#8C8C8C", time: "#15744b" },
  closed: { name: "#8C8C8C", muted: "#BFBFBF", time: "#BFBFBF" },
} as const;

/** Timestamp relativo: hoy → HH:mm, ayer → Yesterday, sino dd/mm/yy. */
function relTime(iso: string | null): string {
  if (!iso) return "";
  const d = dayjs(iso);
  if (d.isSame(dayjs(), "day")) return d.format("HH:mm");
  if (d.isSame(dayjs().subtract(1, "day"), "day")) return "Ayer";
  return d.format("DD/MM/YY");
}

/** Clave de caché de resolución de nombre: store + teléfono normalizado. */
function phoneKey(c: ConversationListItem): string {
  return `${c.store_code}:${phoneFromThread(c.external_thread_id)}`;
}

const ExpandIcon = () => (
  <svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 0.25H8C10.0711 0.25 11.75 1.92893 11.75 4V6C11.75 8.07107 10.0711 9.75 8 9.75H4C1.92893 9.75 0.25 8.07107 0.25 6V4C0.25 1.92893 1.92893 0.25 4 0.25Z" fill="white"/>
    <path d="M4 0.25H8C10.0711 0.25 11.75 1.92893 11.75 4V6C11.75 8.07107 10.0711 9.75 8 9.75H4C1.92893 9.75 0.25 8.07107 0.25 6V4C0.25 1.92893 1.92893 0.25 4 0.25Z" stroke="#D9D9D9" strokeWidth="0.5"/>
    <path d="M5.76563 2.1875H6.23438C6.27604 2.1875 6.29688 2.20833 6.29688 2.25V7.75C6.29688 7.79167 6.27604 7.8125 6.23438 7.8125H5.76563C5.72396 7.8125 5.70313 7.79167 5.70313 7.75V2.25C5.70313 2.20833 5.72396 2.1875 5.76563 2.1875Z" fill="black" fillOpacity="0.88"/>
    <path d="M3.375 4.70313H8.625C8.66667 4.70313 8.6875 4.72396 8.6875 4.76563V5.23438C8.6875 5.27604 8.66667 5.29688 8.625 5.29688H3.375C3.33333 5.29688 3.3125 5.27604 3.3125 5.23438V4.76563C3.3125 4.72396 3.33333 4.70313 3.375 4.70313Z" fill="black" fillOpacity="0.88"/>
  </svg>
);

const CollapseIcon = () => (
  <svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 0.25H8C10.0711 0.25 11.75 1.92893 11.75 4V6C11.75 8.07107 10.0711 9.75 8 9.75H4C1.92893 9.75 0.25 8.07107 0.25 6V4C0.25 1.92893 1.92893 0.25 4 0.25Z" fill="white"/>
    <path d="M4 0.25H8C10.0711 0.25 11.75 1.92893 11.75 4V6C11.75 8.07107 10.0711 9.75 8 9.75H4C1.92893 9.75 0.25 8.07107 0.25 6V4C0.25 1.92893 1.92893 0.25 4 0.25Z" stroke="#D9D9D9" strokeWidth="0.5"/>
    <path d="M4 5H8.2" stroke="black" strokeWidth="0.6" strokeLinecap="square"/>
  </svg>
);

export const Inbox = ({
  conversations,
  loading,
  selectedId,
  onSelect,
  search,
  onSearch,
  quickFilter,
  onQuickFilter,
  onOpenFilters,
  filtersActive,
  channels,
  writableChannels,
  onNewMessage,
  onStatusChange,
  onOperatorClick,
  width = 327,
}: {
  conversations: ConversationListItem[];
  loading: boolean;
  selectedId?: string;
  onSelect: (c: ConversationListItem) => void;
  search: string;
  onSearch: (v: string) => void;
  quickFilter: QuickFilter;
  onQuickFilter: (q: QuickFilter) => void;
  onOpenFilters: () => void;
  filtersActive: boolean;
  /** Todos los accesibles: alimentan las etiquetas de cada fila. */
  channels: Channel[];
  /** Sólo los escribibles: son los únicos donde el operador puede ARRANCAR una conversación. */
  writableChannels: Channel[];
  onNewMessage: (channelId: string) => void;
  /** Recarga la bandeja después de cerrar/reabrir, para que el filtro por estado quede al día. */
  onStatusChange: () => void;
  /** Click en el dueño: va a su ficha de contacto, igual que en la ficha del presupuesto. */
  onOperatorClick: (contactId: number) => void;
  width?: number;
}) => {
  const { token } = theme.useToken();
  // Bug 27 (#109): las conversaciones cuyo contacto está registrado deben mostrar el nombre,
  // no el número. El backend no siempre lo trae en la lista → lo resolvemos por teléfono
  // (igual que el header del chat), con caché por (store,teléfono) y concurrencia limitada.
  const [resolved, setResolved] = useState<Record<string, string>>({});
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  /**
   * Cierra o reabre desde la bandeja, sin tener que entrar al chat.
   *
   * El estado alimenta el filtro por estado del panel de filtros, así que después de cambiarlo hay
   * que recargar: si el filtro está en "Abiertas", una conversación recién cerrada tiene que
   * desaparecer de la lista.
   */
  function toggleExpanded(id: string, e: React.MouseEvent) {
    e.stopPropagation();
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function toggleStatus(c: ConversationListItem, isClosed: boolean) {
    try {
      await conversationsApi.patchStatus(c.conversation_id, isClosed ? "open" : "closed");
      onStatusChange();
    } catch (e: any) {
      antdMessage.error(
        `No se pudo ${isClosed ? "reabrir" : "cerrar"} la conversación: ${
          e?.response?.data?.message ?? e?.message ?? e
        }`,
      );
    }
  }
  const attempted = useRef<Set<string>>(new Set());
  const [resolvedParticipants, setResolvedParticipants] = useState<Record<number, ContactParticipation[]>>({});
  const attemptedContacts = useRef<Set<number>>(new Set());

  useEffect(() => {
    const jobs = new Map<string, ConversationListItem>();
    for (const c of conversations) {
      const k = phoneKey(c);
      if (!c.contact_name && !attempted.current.has(k)) jobs.set(k, c);
    }
    if (jobs.size === 0) return;
    let cancelled = false;
    void (async () => {
      const entries = [...jobs.entries()];
      const CONC = 4;
      for (let i = 0; i < entries.length && !cancelled; i += CONC) {
        await Promise.all(
          entries.slice(i, i + CONC).map(async ([k, c]) => {
            attempted.current.add(k);
            try {
              // Si la fila ya trae `contact_id`, la ficha se pide por clave primaria. La búsqueda
              // por teléfono (`LIKE '%dígitos%'`, sin índice y con temporary+filesort) queda sólo
              // para los hilos que todavía no tienen contacto vinculado. Ver Q6/#136.
              if (c.contact_id) {
                const detail = await contactsApi.getContactDetail(c.contact_id);
                const nombre = [detail?.firstname, detail?.lastname].filter(Boolean).join(" ").trim();
                if (nombre && !cancelled) setResolved((p) => ({ ...p, [k]: nombre }));
                return;
              }
              const hit = await contactsApi.findByPhone(c.external_thread_id, c.store_code);
              if (hit?.name && !cancelled) setResolved((p) => ({ ...p, [k]: hit.name }));
            } catch {
              /* sin match → se queda el número */
            }
          }),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [conversations]);

  useEffect(() => {
    const jobs: number[] = [];
    for (const c of conversations) {
      if (c.contact_id && !attemptedContacts.current.has(c.contact_id)) {
        jobs.push(c.contact_id);
      }
    }
    if (jobs.length === 0) return;
    let cancelled = false;
    void (async () => {
      const CONC = 4;
      for (let i = 0; i < jobs.length && !cancelled; i += CONC) {
        await Promise.all(
          jobs.slice(i, i + CONC).map(async (contactId) => {
            attemptedContacts.current.add(contactId);
            try {
              const detail = await contactsApi.getContactDetail(contactId);
              if (!cancelled && detail.participations.length > 0) {
                setResolvedParticipants((prev) => ({ ...prev, [contactId]: detail.participations }));
              }
            } catch {
              /* sin match → se queda sin participantes */
            }
          }),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [conversations]);

  const displayName = (c: ConversationListItem): string =>
    c.contact_name || resolved[phoneKey(c)] || phoneFromThread(c.external_thread_id) || c.external_thread_id;

  return (
    <div style={{ width, minWidth: width, borderRight: "1px solid #f0f0f0", background: "#fff", display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Barra de búsqueda y filtros rápidos */}
      <div style={{ background: token.colorBgContainer, borderBottom: "1px solid #d9d9d9", padding: "10px 12px", display: "flex", flexDirection: "column", gap: 8, height: 100, boxSizing: "border-box" }}>
        <Flex gap={10} align="center">
          <Input.Search
            allowClear
            placeholder="Buscar por nombre, teléfono o e-mail"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          <Flex gap={4} align="center">
            <Badge dot={filtersActive}>
              <Button shape="circle" icon={<FilterOutlined />} onClick={onOpenFilters} />
            </Badge>
            <Divider type="vertical" style={{ height: 12, margin: 0 }} />
            {/* Mismo desplegable que usa compartir un presupuesto (P9): un solo componente para las dos. */}
            <ChannelPickerDropdown
              channels={writableChannels}
              emptyText="No tenés canales con permiso de envío en este store"
              onPick={onNewMessage}
            >
              <Tooltip title="Nuevo mensaje">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  style={{ background: "#15744b" }}
                />
              </Tooltip>
            </ChannelPickerDropdown>
          </Flex>
        </Flex>

        <Flex gap={4}>
          {QUICK.map((q) => {
            const active = quickFilter === q.key;
            return (
              <Button
                key={q.key}
                size="small"
                onClick={() => onQuickFilter(q.key)}
                style={{
                  background: "#f0f0f0",
                  borderColor: active ? "#15744b" : "#d9d9d9",
                  color: active ? "#15744b" : "rgba(0,0,0,0.88)",
                  borderRadius: 4,
                  height: 22,
                  padding: "0 8px",
                  fontSize: 14,
                }}
              >
                {q.label}
              </Button>
            );
          })}
        </Flex>
      </div>

      {/* Lista */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {loading ? (
          <div style={{ padding: 12 }}>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} active paragraph={{ rows: 2 }} style={{ marginBottom: 16 }} />
            ))}
          </div>
        ) : conversations.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center" }}>
            <Text type="secondary">Sin conversaciones</Text>
          </div>
        ) : (
          conversations.map((c) => {
            const closed = c.status === "closed";
            const isSelected = c.conversation_id === selectedId;
            const unread = c.unread_count > 0 && !closed;
            const palette = ITEM.open;
            // Sólo los dueños: uno del store y uno por seller, con la misma regla que la ficha
            // del presupuesto. Antes se mostraban TODAS las participaciones del contacto.
            const participants = ownerParticipations(
              c.contact_id ? (resolvedParticipants[c.contact_id] ?? []) : [],
            );

            const tooltipStyle = {
              color: token.colorBgContainer,
              overlayInnerStyle: {
                color: token.colorText,
                fontSize: 11,
                padding: "2px 8px",
                minHeight: "auto",
                border: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
              } as React.CSSProperties,
            };

            return (
              <div
                key={c.conversation_id}
                onClick={() => onSelect(c)}
                style={{ padding: 4, boxSizing: "border-box", cursor: "pointer" }}
              >
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                  padding: isSelected && !closed ? 4 : 8,
                  borderRadius: isSelected && !closed ? 6 : 0,
                  background: isSelected && !closed ? "rgba(0,0,0,0.02)" : "#fff",
                  boxShadow: isSelected && !closed ? "0px 2px 4px 0px rgba(0,0,0,0.08)" : "none",
                  overflow: "hidden",
                  boxSizing: "border-box",
                }}>

                  {/* Fila 1: nombre | canal */}
                  <Flex justify="space-between" align="center" gap={8}>
                    <Text style={{ fontSize: 14, fontWeight: 700, color: palette.name }} ellipsis>
                      {displayName(c)}
                    </Text>
                    <Flex align="center" gap={4} style={{ flexShrink: 0 }}>
                      {isCloudApi(c.channel_type) ? <WhatsAppMetaIcon style={{ fontSize: 14 }} /> : <WhatsAppQrIcon style={{ fontSize: 14 }} />}
                      <Text style={{ fontSize: 12, color: palette.muted, fontWeight: 600 }}>
                        {c.channel_name}
                      </Text>
                    </Flex>
                  </Flex>

                  {/* Fila 2: operadores del contacto (store primero, seller después) + timestamp/indicador a la derecha. */}
                  {(() => {
                    const sellerParts = participants.filter((p) => p.type === "seller");
                    const hasMoreSellers = sellerParts.length > 1;
                    const isExpanded = expandedIds.has(c.conversation_id);
                    const visibleParticipants = participants.length > 0
                      ? [
                          ...participants.filter((p) => p.type === "store"),
                          ...(hasMoreSellers && !isExpanded ? sellerParts.slice(0, 1) : sellerParts),
                        ]
                      : [];

                    return (
                  <Flex justify="space-between" align="flex-start" gap={4}>
                    <Flex vertical gap={2} style={{ minWidth: 0 }}>
                      {visibleParticipants.length > 0 ? visibleParticipants.map((p, idx) => (
                        <Flex key={`${p.userId ?? idx}`} align="center" gap={8}>
                          <Flex align="center" gap={2}>
                            {p.type === "seller"
                              ? <SellerIcon style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }} />
                              : <ShopOutlined style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }} />
                            }
                            <Text style={{ fontSize: 10, color: "rgba(0,0,0,0.65)" }}>
                              {p.identifier || c.store_code}
                            </Text>
                          </Flex>
                          {p.name && p.name !== "—" ? (
                            <Flex align="center" gap={2}>
                              <UserOutlined style={{ fontSize: 12, color: "#15744b" }} />
                              <Text
                                onClick={p.contactId ? (e) => { e.stopPropagation(); onOperatorClick(p.contactId!); } : undefined}
                                style={{ fontSize: 10, color: "#15744b", fontWeight: 700, cursor: p.contactId ? "pointer" : "default" }}
                              >
                                {p.name}
                              </Text>
                            </Flex>
                          ) : (
                            <Text style={{ fontSize: 10, color: "rgba(0,0,0,0.45)", fontStyle: "italic" }}>Sin dueño</Text>
                          )}
                          {hasMoreSellers && p === sellerParts[0] && (
                            <div
                              onClick={(e) => toggleExpanded(c.conversation_id, e)}
                              style={{ display: "flex", alignItems: "center", cursor: "pointer", flexShrink: 0 }}
                            >
                              {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
                            </div>
                          )}
                        </Flex>
                      )) : (<>
                        <Flex align="center" gap={8}>
                          <Flex align="center" gap={2}>
                            <ShopOutlined style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }} />
                            <Text style={{ fontSize: 10, color: "rgba(0,0,0,0.65)" }}>{c.store_code}</Text>
                          </Flex>
                          <Text style={{ fontSize: 10, color: "rgba(0,0,0,0.45)", fontStyle: "italic" }}>Sin dueño</Text>
                        </Flex>
                        <Flex align="center" gap={8}>
                          <SellerIcon style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }} />
                          <Text style={{ fontSize: 10, color: "rgba(0,0,0,0.45)", fontStyle: "italic" }}>Sin dueño</Text>
                        </Flex>
                      </>)}
                    </Flex>

                    {/* Timestamp + indicador alineados con las filas de participantes */}
                    <Flex vertical align="flex-end" justify="space-between" style={{ alignSelf: "stretch", flexShrink: 0 }}>
                      <Text style={{ fontSize: 12, color: unread ? palette.time : palette.muted, fontWeight: unread ? 600 : 400, lineHeight: "20px" }}>
                        {relTime(c.last_message_at)}
                      </Text>
                      {unread ? (
                        <Badge count={c.unread_count || 1} size="small" color="#15744b" />
                      ) : !closed ? (
                        <Tooltip title="Cerrar" color={tooltipStyle.color} overlayInnerStyle={tooltipStyle.overlayInnerStyle}>
                          <div
                            onClick={(e) => { e.stopPropagation(); void toggleStatus(c, false); }}
                            style={{ width: 14, height: 14, borderRadius: "50%", background: "#15744b", cursor: "pointer", flexShrink: 0 }}
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Abrir" color={tooltipStyle.color} overlayInnerStyle={tooltipStyle.overlayInnerStyle}>
                          <div
                            onClick={(e) => { e.stopPropagation(); void toggleStatus(c, true); }}
                            style={{ width: 14, height: 14, borderRadius: "50%", border: "1.5px solid #D9D9D9", cursor: "pointer", flexShrink: 0 }}
                          />
                        </Tooltip>
                      )}
                    </Flex>
                  </Flex>
                    );
                  })()}

                  {/* Fila 3: preview */}
                  <div style={{ minWidth: 0, height: 40, overflow: "hidden" }}>
                    <Text style={{ fontSize: 12, color: palette.muted, display: "block", lineHeight: "20px", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {c.last_message_text ?? "—"}
                    </Text>
                  </div>

                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
